import {Api, Method} from "../api/api";
import {LinkPager} from "../link-pager";
import {PlayVideo} from "../play-video";
import {initProductSlider} from "../productSlider";
import {getProductCardOldTemplate} from "../show-more/template";
import {closeModal, fadeIn, fadeOut, formatedPrice, getDeclension, openModal, renderElement} from "../utils/common";
import {commonEventEmitter} from "../commonEventEmitter";

/**
 * Функционал отвечающий за ajax фильтрацию на странице каталога
 *
 * @class CatalogFilter
 */
class CatalogFilter {
  #catalogElement;
  #filterElement;
  #mainFilterElement;
  #filterFormElement;
  #openFilterBtnElement;
  #closeFilterBtnElement;
  #filterBtnElements;
  #prevFilterElement;
  #resetBtnElements;
  #applyBtnElements;
  #filterItemElements;
  #resetFormBtnElement;
  #mainFilterInputElements;
  #filterInputElements;
  #filterAmountElements;
  #catalogListElement;
  #playVideo;
  #paginationElement;
  #linkPager;
  #showMoreBtnElement;
  #perPage;
  #currentPage;
  #pagesAmount;
  #productsAmount;

  #body = document.body;
  #api = new Api();
  #isLoading = false;

  #eventEmitter = commonEventEmitter;

  constructor() {
    this.#catalogElement = document.querySelector(".js-catalog");
    this.#mainFilterElement = document.querySelector(".js-main-filter");

    if (this.#catalogElement && this.#mainFilterElement) {
      this.#init();
    }
  }

  /**
   * Инициализация параметров
   *
   * @memberof CatalogFilter
   */
  #init() {
    this.#playVideo = new PlayVideo({autoInit: false});
    this.#paginationElement = this.#catalogElement.querySelector(".js-pagination");
    this.#filterElement = this.#catalogElement.querySelector(".js-filter-element");
    this.#filterFormElement = this.#mainFilterElement.querySelector(".js-filter-inform");
    this.#openFilterBtnElement = this.#catalogElement.querySelector(".js-filter-open");
    this.#closeFilterBtnElement = this.#mainFilterElement.querySelector(".js-filter-close");
    this.#filterBtnElements = this.#filterElement.querySelectorAll(".js-filter-btn");
    this.#resetBtnElements = this.#filterElement.querySelectorAll(".js-reset-btn");
    this.#applyBtnElements = this.#filterElement.querySelectorAll(".js-filter-apply");
    this.#filterItemElements = Array.from(this.#filterElement.querySelectorAll(".js-filter-item"));
    this.#resetFormBtnElement = this.#filterElement.querySelector(".js-reset-form");
    this.#mainFilterInputElements = Array.from(this.#filterFormElement.querySelectorAll("input[id]"));
    this.#filterInputElements = Array.from(this.#filterElement.querySelectorAll("input[data-id]"));
    this.#filterAmountElements = this.#catalogElement.querySelectorAll(".js-filter-amount");
    this.#catalogListElement = this.#catalogElement.querySelector(".js-catalog-list");
    this.#showMoreBtnElement = this.#catalogElement.querySelector(".js-show-more-btn");

    if (this.#showMoreBtnElement && this.#paginationElement) {
      this.#perPage = +this.#showMoreBtnElement.dataset.perPage;
      this.#currentPage = +this.#showMoreBtnElement.dataset.currentPage;
      this.#linkPager = new LinkPager(this.#paginationElement);
    }

    this.#addListeners();
  }

  #bindListeners() {
    this.onOpenFilterBtnElementClick = this.onOpenFilterBtnElementClick.bind(this);
    this.onCloseFilterBtnElementClick = this.onCloseFilterBtnElementClick.bind(this);
    this.onFilterElementChange = this.onFilterElementChange.bind(this);
    this.onFormReset = this.onFormReset.bind(this);
    this.onFilterFormElementChange = this.onFilterFormElementChange.bind(this);
    this.onBodyElementClick = this.onBodyElementClick.bind(this);
    this.onFilterBtnElementClick = this.onFilterBtnElementClick.bind(this);
    this.onResetBtnElementClick = this.onResetBtnElementClick.bind(this);
    this.onApplyBtnElementClick = this.onApplyBtnElementClick.bind(this);
    this.onFilterFormElementSubmit = this.onFilterFormElementSubmit.bind(this);
    this.resetForm = this.resetForm.bind(this);
    this.setFilter = this.setFilter.bind(this);
  }

  /**
   * Добавление слушателей на элементы
   *
   * @memberof CatalogFilter
   */
  #addListeners() {
    this.#bindListeners();

    this.#openFilterBtnElement.addEventListener("click", this.onOpenFilterBtnElementClick);
    this.#closeFilterBtnElement.addEventListener("click", this.onCloseFilterBtnElementClick);
    this.#filterElement.addEventListener("change", this.onFilterElementChange);
    this.#filterElement.addEventListener("reset", this.onFormReset);
    this.#filterFormElement.addEventListener("change", this.onFilterFormElementChange);
    this.#filterFormElement.addEventListener("reset", this.onFormReset);
    this.#filterFormElement.addEventListener("submit", this.onFilterFormElementSubmit);
    this.#filterBtnElements.forEach((element) => element.addEventListener("click", this.onFilterBtnElementClick));
    this.#resetBtnElements.forEach((element) => element.addEventListener("click", this.onResetBtnElementClick));
    this.#applyBtnElements.forEach((element) => element.addEventListener("click", this.onApplyBtnElementClick));

    this.#eventEmitter.on(`catalog-filter.reset`, this.resetForm);
    this.#eventEmitter.on(`catalog-filter.set-filter`, this.setFilter);
  }

  /**
   * Показ блока с главной фильтрацией
   *
   * @memberof CatalogFilter
   */
  #showMainFilterElement() {
    this.#body.style.overflow = "hidden";
    openModal(this.#mainFilterElement, "flex");
    this.#body.classList.add("no-plugins");

    setTimeout(() => {
      this.#filterFormElement.classList.add("filter--show");
    }, 300);
  }

  /**
   * Скрытие блока с главной фильтрацией
   *
   * @memberof CatalogFilter
   */
  #hideMainFilterElement() {
    this.#body.style.overflow = "";
    this.#filterFormElement.classList.remove("filter--show");
    this.#body.classList.remove("no-plugins");

    setTimeout(() => {
      closeModal(this.#mainFilterElement);
    }, 300);
  }

  /**
   * Закрытие другого открытого элемента попапа
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #closeOtherFilterPopup(filterItem) {
    if (this.#prevFilterElement && this.#prevFilterElement !== filterItem) {
      this.#hideFilterPopup(this.#prevFilterElement);
    }
  }

  /**
   * Показ элемента попапа
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #showFilterPopup(filterItem) {
    filterItem.classList.add("filter-item--show");

    this.#body.addEventListener("click", this.onBodyElementClick);
  }

  /**
   * Скрытие элемента попапа
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #hideFilterPopup(filterItem) {
    filterItem.classList.remove("filter-item--show");

    this.#body.removeEventListener("click", this.onBodyElementClick);
  }

  /**
   * Переключение активного состояния кнопки
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #filterActiveClassToggle(filterItem) {
    filterItem.classList.toggle("filter-item--active");

    if (filterItem.dataset.type === "available") {
      const availableInput = this.#mainFilterInputElements.find((element) => element.id === filterItem.dataset.type);

      if (availableInput) availableInput.checked = filterItem.classList.contains("filter-item--active");
    }
  }

  /**
   * Показ/скрытие кнопки сброса всех фильтров в зависимости хотя бы одного активного пункта
   *
   * @memberof CatalogFilter
   */
  #setResetBtnVisibility() {
    const isFilterActivated = this.#filterItemElements.find((item) => item.classList.contains("filter-item--active"));

    if (isFilterActivated) {
      this.#resetFormBtnElement.classList.add("filter-item__reset--show");
    } else {
      this.#resetFormBtnElement.classList.remove("filter-item__reset--show");
    }
  }

  /**
   * Добавление активного класса элементу фильтра
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #activateFilterItem(filterItem) {
    filterItem.classList.add("filter-item--active");
  }

  /**
   * Удаление активного класса у элемента фильтра
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #deactivateFilterItem(filterItem) {
    filterItem.classList.remove("filter-item--active");
  }

  /**
   * Установка текстового содержимого внутрь кнопки
   *
   * @param {string} text - текст для установки
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #setResultTextContent(filterItem, text = "") {
    const resultTextElement = filterItem.querySelector(".js-filter-result");
    resultTextElement.textContent = text;
  }

  /**
   * Сброс фильтра с радио инпутами
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #resetRadioFilter(filterItem) {
    const defaultInputElement = filterItem.querySelectorAll("input[type='radio']")[0];
    defaultInputElement.checked = true;

    this.#setResultTextContent(filterItem, defaultInputElement.value);
  }

  /**
   * Сброс фильтра с ценами
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #resetPriceFilter(filterItem) {
    const minPriceInput = filterItem.querySelector("input[data-id='price-1']");
    const maxPriceInput = filterItem.querySelector("input[data-id='price-2']");

    this.#setResultTextContent(filterItem);
    minPriceInput.value = "";
    maxPriceInput.value = "";
    this.#synchronizeMainFilterInputs(minPriceInput);
    this.#synchronizeMainFilterInputs(maxPriceInput);
  }

  /**
   * Установка значений выбранных чекбоксов
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #setCheckboxResultText(filterItem) {
    let text = "";
    const checkboxElements = Array.from(filterItem.querySelectorAll("input[type='checkbox']"));
    const checkedElements = checkboxElements.filter((element) => element.checked);

    if (checkedElements.length === 0) {
      this.#deactivateFilterItem(filterItem);
    } else {
      text = checkedElements.length > 1 ? checkedElements.length : checkedElements[0].value;
      this.#activateFilterItem(filterItem);
    }

    this.#setResultTextContent(filterItem, text);
    this.#setResetBtnVisibility();
  }

  /**
   * Обновление разметки пагинации после выполнения ajax запроса по рендеру карточек
   *
   * @param {number} currentPage - Номер текущей страницы
   * @param {number} pagesAmount - Общее число страниц
   * @param {string} searchParams - Строка состоящая из параметров фильтрации
   * @param {number} perPage - Кол-во элементов необходимых для рендера для одной страницы
   * @memberof CatalogFilter
   */
  #updatePagination(currentPage, pagesAmount, searchParams, perPage) {
    this.#paginationElement.innerHTML = this.#linkPager.getUpdatedPaginationElement(currentPage, pagesAmount, searchParams, perPage);
  }

  /**
   * Установка значений выбранного ценового диапазона
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #setPriceResultText(filterItem) {
    let text = "";
    const minPriceInput = filterItem.querySelector("input[data-id='price-1']");
    const maxPriceInput = filterItem.querySelector("input[data-id='price-2']");
    const minPrice = Boolean(minPriceInput.value) ? minPriceInput.value : minPriceInput.min;
    const maxPrice = Boolean(maxPriceInput.value) ? maxPriceInput.value : maxPriceInput.max;

    if (Boolean(minPriceInput.value) || Boolean(maxPriceInput.value)) {
      text = `${formatedPrice(minPrice)} - ${formatedPrice(maxPrice)}`;
      this.#activateFilterItem(filterItem);
    } else {
      this.#deactivateFilterItem(filterItem);
    }

    this.#setResultTextContent(filterItem, text);
  }

  /**
   * Сброс фильтра с чекбоксами
   *
   * @param {HTMLElement} filterItem - Элемент фильтрации
   * @memberof CatalogFilter
   */
  #resetCheckboxFilter(filterItem) {
    const checkboxElements = filterItem.querySelectorAll("input[type='checkbox']");

    checkboxElements.forEach((element) => {
      element.checked = false;
      this.#synchronizeMainFilterInputs(element);
    });

    this.#setResultTextContent(filterItem);
  }

  /**
   * Сброс необходимого фильтра
   *
   * @memberof CatalogFilter
   */
  #resetFilterElement() {
    this.#filterItemElements.forEach((element) => {
      switch (element.dataset.type) {
        case "sale":
        case "available":
          break;
        case "sort":
          this.#resetRadioFilter(element);
          break;
        case "price":
          this.#resetPriceFilter(element);
          break;
        default:
          this.#resetCheckboxFilter(element);
          break;
      }

      this.#deactivateFilterItem(element);
    });

    this.#resetFormBtnElement.classList.remove("filter-item__reset--show");
  }

  /**
   * Сброс всех элементов главного фильтра
   *
   * @memberof CatalogFilter
   */
  #resetMainFilterElement() {
    this.#mainFilterInputElements
      .filter((input) => input.type === `checkbox`)
      .forEach((input) => {
        input.checked = false;
      });
  }

  /**
   * Обновление элементов фильтрации
   *
   * @memberof CatalogFilter
   */
  #updateFilterElement() {
    const requiredElements = this.#filterItemElements.filter(
      (element) => element.dataset.type !== "sale" && element.dataset.type !== "available" && element.dataset.type !== "sort",
    );

    requiredElements.forEach((element) => {
      if (element.dataset.type === "price") {
        this.#setPriceResultText(element);
      } else {
        this.#setCheckboxResultText(element);
      }
    });
  }

  /**
   * Синхронизация параметров фильтрации главного фильтра
   *
   * @param {HTMLInputElement} input
   * @memberof CatalogFilter
   */
  #synchronizeMainFilterInputs(input) {
    const requiredInputElement = this.#mainFilterInputElements.find((element) => element.id === input.dataset.id);

    if (requiredInputElement) {
      if (input.type === "number") {
        requiredInputElement.value = input.value;
      } else {
        requiredInputElement.checked = input.checked;
      }
    }
  }

  /**
   * Синхронизация параметров фильтрации малого фильтра
   *
   * @param {HTMLInputElement} input
   * @memberof CatalogFilter
   */
  #synchronizeFilterInputs(input) {
    const requiredInputElement = this.#filterInputElements.find((element) => element.dataset.id === input.id);
    const availableFilterItem = this.#filterItemElements.find((element) => element.dataset.type === input.id);

    if (requiredInputElement) {
      if (input.type === "number") {
        requiredInputElement.value = input.value;
      } else {
        requiredInputElement.checked = input.checked;
      }
    }

    if (availableFilterItem) {
      input.checked ? availableFilterItem.classList.add("filter-item--active") : availableFilterItem.classList.remove("filter-item--active");
    }
  }

  /**
   * Установка значения начальной страницы после рендера карточек
   *
   * @memberof CatalogFilter
   */
  #setInitialPageNumber() {
    this.#showMoreBtnElement.dataset.currentPage = 1;
    this.#currentPage = 1;
  }

  /**
   * Установка состояние загрузки во время отправки запроса
   *
   * @param {Boolean} isLoading
   * @memberof CatalogFilter
   */
  #setLoadingStatus(isLoading) {
    isLoading ? this.#catalogElement.classList.add("catalog--loading") : this.#catalogElement.classList.remove("catalog--loading");
  }

  /**
   * Отправка запроса на получения предварительного кол-ва товаров по выбранным параметрам
   *
   * @memberof CatalogFilter
   */
  async #fetchPreFilteredAmount() {
    const formData = this.#getFormData();
    const url = "/actions/ajax-product-action/pre-filter";
    // const url = `/data/pre-filter.json`;

    formData.append("cathegory", document.location.pathname);
    formData.append("isDiscount", this.#getDiscountValue());

    const {code, answer} = await this.#api.customQuery(url, {method: Method.POST, query: formData});

    if (code === 200) {
      this.#updateFilterAmount(answer.data);

      return answer.data;
    }
  }

  /**
   * Инициализация слайдера внутри карточки продукта (перелистывание изображения по движению курсора)
   *
   * @param {HTMLElement} element -Элемент слайдера
   * @memberof CatalogFilter
   */
  #initSlider(element) {
    initProductSlider(element);
  }

  /**
   * Инициализация слушателей на элемент видео в карточке товара
   *
   * @param {HTMLVideoElement} element - Элемент видео
   * @memberof CatalogFilter
   */
  #initVideo(element) {
    this.#playVideo.addListeners(element);
  }

  /**
   * Получение значения параметра сортировки
   *
   * @return {string}
   * @memberof CatalogFilter
   */
  #getSortValue() {
    const sortInputElement = this.#filterInputElements.filter((element) => element.name === "sort").find((element) => element.checked);

    return !sortInputElement ? "" : sortInputElement.dataset.id;
  }

  /**
   * Получение значения параметра фильтрации по наличию скидке
   *
   * @return {Number}
   * @memberof CatalogFilter
   */
  #getDiscountValue() {
    const saleElement = this.#filterItemElements.find((element) => element.dataset.type === "sale");

    return saleElement && saleElement.classList.contains("filter-item--active") ? 1 : "";
  }

  /**
   * Рендер необходимых элементов после загрузки данных
   *
   * @param {Array} dataList - Массив с необходимыми данными для рендера элементов
   * @memberof CatalogFilter
   */
  #renderProducts(dataList) {
    this.#catalogListElement.innerHTML = "";

    dataList.forEach((dataElement, index) => {
      const renderedElement = renderElement(this.#catalogListElement, getProductCardOldTemplate(dataElement, index + 1));
      const sliderElement = renderedElement.querySelector(".js-item-slider");
      const videoElement = renderedElement.querySelector(".js-item-video");

      if (sliderElement) {
        this.#initSlider(sliderElement);
      }

      if (videoElement) {
        this.#initVideo(videoElement);
      }
    });
  }

  /**
   * Получение строки с параметрами фильтрации
   *
   * @return {string}
   * @memberof CatalogFilter
   */
  #getSearchParams() {
    const formData = this.#getFormData();
    const searchParams = new URLSearchParams();

    for (let [key, value] of formData) {
      searchParams.append(key, value);
    }

    searchParams.append("sort", this.#getSortValue());
    searchParams.append("isDiscount", this.#getDiscountValue());

    return searchParams;
  }

  #scrollToCatalogTop() {
    if (window.scrollY >= 600) {
      this.#catalogElement.scrollIntoView();
    }
  }

  /**
   * Отправка запроса на получение отфильтрованных карточек
   *
   * @memberof CatalogFilter
   */
  async #fetchProductCards() {
    this.#isLoading = true;
    this.#setLoadingStatus(this.#isLoading);
    const url = this.#filterFormElement.action;
    const searchParams = this.#getSearchParams();

    const {code, answer} = await this.#api.customQuery(`${url}?${searchParams}&ajax=1`, {});

    if (code === 200) {
      this.#isLoading = false;
      this.#setLoadingStatus(this.#isLoading);

      if (answer.data.length > 0) {
        this.#renderProducts(answer.data);
        this.#scrollToCatalogTop();
        window.history.pushState({}, "", `${location.pathname}?${searchParams}`);

        if (this.#showMoreBtnElement) {
          this.#setInitialPageNumber();
          this.#updatePagination(this.#currentPage, Math.ceil(this.#productsAmount / this.#perPage), `${url}?${searchParams}`, this.#perPage);

          if (answer.data.length > this.#perPage) {
            fadeIn(this.#showMoreBtnElement, 200, "flex");
          } else {
            fadeOut(this.#showMoreBtnElement, 200);
          }
        }
      }
    } else {
      this.#isLoading = false;
      this.#setLoadingStatus(this.#isLoading);
    }
  }

  /**
   * Получение кол-ва выбранных параметров
   *
   * @returns {number}
   * @memberof CatalogFilter
   */
  #getSelectedFilters() {
    const selectedParams = new Set();

    this.#mainFilterInputElements
      .filter((input) => input.checked || (input.type === "number" && Boolean(input.value)))
      .forEach((input) => selectedParams.add(input.name));

    return selectedParams.size;
  }

  /**
   * Установка значения кол-ва выбранных параметров на элемент кнопки открытия главного фильтра
   *
   * @memberof CatalogFilter
   */
  #setSelectedFilters() {
    this.#openFilterBtnElement.dataset.selected = this.#getSelectedFilters();
  }

  /**
   * Обновление значения текстового контента с общим кол-вом товаров предварительной фильтрации
   *
   * @param {number} amount - кол-во товаров предварительной фильтрации
   * @memberof CatalogFilter
   */
  #updateFilterAmount(amount) {
    this.#productsAmount = amount;

    const text =
      amount !== 0
        ? `${formatedPrice(this.#productsAmount)} ${getDeclension(this.#productsAmount, ["товар", "товара", "товаров"])}`
        : "Товары не найдены. Измените фильтры";

    this.#filterAmountElements.forEach((item) => {
      item.textContent = text;

      if (amount === 0) {
        item.parentElement.dataset.notFound = true;
      } else {
        item.parentElement.dataset.notFound = false;
      }
    });
  }

  /**
   * Получение данных формы
   *
   * @returns {FormData}
   * @memberof CatalogFilter
   */
  #getFormData() {
    const formData = new FormData(this.#filterFormElement);

    return formData;
  }

  /**
   * @param {HTMLElement} filterElement
   */
  #setFilterItemResult(filterElement) {
    if (filterElement.dataset.type === "price") {
      this.#setPriceResultText(filterElement);
    } else {
      this.#setCheckboxResultText(filterElement);
    }
  }

  resetForm() {
    this.#resetFilterElement();
    this.#resetMainFilterElement();
    this.#hideMainFilterElement();
  }

  async setFilter({name, value, isChecked}) {
    const currentMainInput =
      Array.from(this.#filterFormElement[name]).length !== 0
        ? Array.from(this.#filterFormElement[name]).find((element) => element.value === value)
        : this.#filterFormElement[name];

    if (!currentMainInput) return;

    currentMainInput.checked = isChecked;

    this.#synchronizeFilterInputs(currentMainInput);

    await this.#fetchPreFilteredAmount();

    this.#updateFilterElement();

    this.#setSelectedFilters();

    if (this.#productsAmount !== 0) {
      this.#fetchProductCards();
    }
  }

  /**
   * Слушатель клика на элемент body
   *
   * @memberof CatalogFilter
   */
  onBodyElementClick({target}) {
    if (this.#prevFilterElement && !target.closest(".js-filter-item")) {
      this.#hideFilterPopup(this.#prevFilterElement);
    }
  }

  /**
   * Слушатель клика на кнопку фильтра определённого параметра
   *
   * @memberof CatalogFilter
   */
  async onFilterBtnElementClick({currentTarget}) {
    const filterElement = currentTarget.closest(".js-filter-item");

    this.#closeOtherFilterPopup(filterElement);

    if (filterElement.classList.contains("filter-item--show")) {
      this.#hideFilterPopup(filterElement);
    } else {
      this.#showFilterPopup(filterElement);
    }

    if (filterElement.dataset.type === "sale" || filterElement.dataset.type === "available") {
      this.#filterActiveClassToggle(filterElement);
      this.#setResetBtnVisibility();
      await this.#fetchPreFilteredAmount();

      if (this.#productsAmount !== 0) {
        this.#fetchProductCards();
      }
    }

    this.#prevFilterElement = filterElement;
  }

  /**
   * Слушатель изменения формы малого фильтра
   *
   * @memberof CatalogFilter
   */
  async onFilterElementChange({target}) {
    const filterElement = target.closest(".js-filter-item");

    if (target.type === "radio") {
      await this.#fetchPreFilteredAmount();
      this.#setResultTextContent(filterElement, target.value);
      this.#activateFilterItem(filterElement);
      this.#hideFilterPopup(filterElement);
      this.#setResetBtnVisibility();
      this.#fetchProductCards();
    } else {
      this.#synchronizeMainFilterInputs(target);
      await this.#fetchPreFilteredAmount();
    }
  }

  /**
   * Слушатель клика на кнопку сброса определённого параметра фильтрации
   *
   * @memberof CatalogFilter
   */
  async onResetBtnElementClick({currentTarget}) {
    const filterElement = currentTarget.closest(".js-filter-item");
    await this.#fetchPreFilteredAmount();

    switch (true) {
      case filterElement.dataset.type === "sort":
        this.#resetRadioFilter(filterElement);
        break;
      case filterElement.dataset.type === "price":
        this.#resetPriceFilter(filterElement);
        break;
      default:
        this.#resetCheckboxFilter(filterElement);
        break;
    }

    this.#deactivateFilterItem(filterElement);
    this.#setResetBtnVisibility();

    if (this.#productsAmount !== 0) {
      await this.#fetchProductCards();

      this.#eventEmitter.emit(`additional-filter.update`);
    }
  }

  /**
   * Слушатель клика на кнопку применения выбранных параметров фильтрации
   *
   * @memberof CatalogFilter
   */
  async onApplyBtnElementClick({currentTarget}) {
    const filterElement = currentTarget.closest(".js-filter-item");
    await this.#fetchPreFilteredAmount();

    this.#setFilterItemResult(filterElement);

    if (this.#productsAmount !== 0) {
      this.#hideFilterPopup(filterElement);
      await this.#fetchProductCards();

      this.#eventEmitter.emit(`additional-filter.update`);
    }
  }

  /**
   * Слушатель сброса формы фильтрации
   *
   * @memberof CatalogFilter
   */
  async onFormReset(event) {
    event.preventDefault();

    this.#resetFilterElement();
    this.#resetMainFilterElement();
    this.#hideMainFilterElement();
    this.#fetchPreFilteredAmount();
    await this.#fetchProductCards();
    this.#eventEmitter.emit(`additional-filter.update`);
  }

  /**
   * Слушатель клика на кнопку открытия главного фильтра
   *
   * @memberof CatalogFilter
   */
  onOpenFilterBtnElementClick() {
    this.#showMainFilterElement();
  }

  /**
   * Слушатель клика на кнопку закрытия главного фильтра
   *
   * @memberof CatalogFilter
   */
  onCloseFilterBtnElementClick() {
    this.#hideMainFilterElement();
  }

  /**
   * Слушатель изменения формы главного фильтра
   *
   * @memberof CatalogFilter
   */
  onFilterFormElementChange({target}) {
    this.#synchronizeFilterInputs(target);

    if (target.type === "checkbox" || target.type === "number") {
      this.#fetchPreFilteredAmount();
    }
  }

  /**
   * Слушатель отправки формы главного фильтра
   *
   * @memberof CatalogFilter
   */
  async onFilterFormElementSubmit(event) {
    event.preventDefault();
    await this.#fetchPreFilteredAmount();

    this.#updateFilterElement();
    this.#hideMainFilterElement();
    this.#setSelectedFilters();

    if (this.#productsAmount !== 0) {
      await this.#fetchProductCards();
      this.#eventEmitter.emit(`additional-filter.update`);
    }
  }
}

export {CatalogFilter};
